import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ChevronDown, ChevronUp, AlertTriangle, Info, ArrowRight } from "lucide-react";
import { CitationValidation } from "@/components/CitationValidation";
import { EligibilityGates } from "@/components/EligibilityGates";

const TABS = [["overview", "Integrity Overview"], ["controls", "Control Register"], ["citations", "Citation Validation"],
  ["eligibility", "Eligibility Gates"], ["issues", "Issues & Exceptions"], ["audit", "Traceability Audit"], ["limitations", "Limitations & Disclosures"]];

const TILE_CLS = { teal: "text-teal-700", amber: "text-amber-600", red: "text-red-600", purple: "text-violet-700", blue: "text-sky-700" };
const CTL_CLS = {
  "Automated control—passing": "bg-teal-50 text-teal-700 border-teal-200",
  "Automated control—warning": "bg-amber-50 text-amber-700 border-amber-200",
  "Automated control—failed": "bg-red-50 text-red-700 border-red-200",
  "Manual review complete": "bg-teal-50 text-teal-700 border-teal-200",
  "Manual review pending": "bg-amber-50 text-amber-700 border-amber-200",
  "Policy requirement": "bg-sky-50 text-sky-700 border-sky-200",
  "Partially implemented": "bg-amber-50 text-amber-700 border-amber-200",
  "Prototype demonstration only": "bg-violet-50 text-violet-700 border-violet-200",
  "Not applicable": "bg-slate-100 text-slate-400 border-slate-200",
};
const SEV_CLS = { Critical: "bg-red-600 text-white border-red-600", High: "bg-red-50 text-red-700 border-red-200",
  Moderate: "bg-amber-50 text-amber-700 border-amber-200", Low: "bg-sky-50 text-sky-700 border-sky-200",
  Informational: "bg-slate-100 text-slate-500 border-slate-200" };
const ISS_CLS = { Open: "bg-amber-50 text-amber-700 border-amber-200", "Under investigation": "bg-sky-50 text-sky-700 border-sky-200",
  "Remediation in progress": "bg-indigo-50 text-indigo-700 border-indigo-200", "Awaiting scientific review": "bg-violet-50 text-violet-700 border-violet-200",
  Resolved: "bg-teal-50 text-teal-700 border-teal-200", "Risk accepted": "bg-slate-100 text-slate-600 border-slate-300",
  "False positive": "bg-slate-100 text-slate-400 border-slate-200", Closed: "bg-slate-100 text-slate-400 border-slate-200" };

function ControlRow({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`control-${c.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-slate-400">{c.id}</span>
        <span className="text-xs font-bold text-slate-800 flex-1 min-w-[220px]">{c.name}</span>
        <Badge variant="outline" className={`text-[9px] ${CTL_CLS[c.status]}`}>{c.status}</Badge>
        {c.open_exceptions > 0 && <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5">{c.open_exceptions} open exception{c.open_exceptions > 1 ? "s" : ""}</span>}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5 text-[11px] text-slate-600 border-t border-slate-100 pt-3" data-testid={`control-detail-${c.id}`}>
          {[["Purpose", c.purpose], ["Control type", c.type], ["Scope", c.scope], ["Implementation method", c.method],
            ["Last executed / reviewed", c.last_executed], ["Last result", c.last_result], ["Control owner", c.owner],
            ["Evidence of operation", c.evidence], ["Required remediation", c.remediation || "None"]].map(([l, v]) => (
            <p key={l}><span className="font-mono text-[9px] uppercase text-slate-400 block">{l}</span>{v}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function IssueRow({ i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`issue-${i.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-slate-400">{i.id}</span>
        <span className="text-xs font-bold text-slate-800 flex-1 min-w-[220px]">{i.type}</span>
        <Badge variant="outline" className={`text-[9px] ${SEV_CLS[i.severity]}`}>{i.severity}</Badge>
        <Badge variant="outline" className={`text-[9px] ${ISS_CLS[i.status]}`}>{i.status}</Badge>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 text-[11px] text-slate-600 space-y-1.5" data-testid={`issue-detail-${i.id}`}>
          <p>{i.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5">
            {[["Affected object", i.object], ["Detection method", i.detection], ["Date detected", i.detected],
              ["Responsible owner", i.owner], ["Due date", i.due]].map(([l, v]) => (
              <p key={l}><span className="font-mono text-[9px] uppercase text-slate-400 block">{l}</span>{v}</p>
            ))}
          </div>
          {i.rationale && <p className="text-slate-500"><span className="font-mono text-[9px] uppercase text-slate-400 block">Status rationale (required for risk-accepted / false-positive / closure)</span>{i.rationale}</p>}
          {(i.downstream || []).length > 0 && (
            <p className="text-amber-700"><span className="font-mono text-[9px] uppercase text-slate-400 block">Downstream objects affected — reassessment required where applicable</span>{i.downstream.join(" · ")}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Integrity() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [ctlF, setCtlF] = useState("all");
  const [sevF, setSevF] = useState("all");
  const [stF, setStF] = useState("all");

  useEffect(() => { api.get("/integrity").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading integrity controls…</div>;

  const controls = data.controls.filter((c) => ctlF === "all" || c.status === ctlF);
  const issues = data.issues.filter((i) => (sevF === "all" || i.severity === sevF) && (stF === "all" || i.status === stF));

  return (
    <div className="space-y-4" data-testid="integrity-view">
      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-teal-400" />
          <h3 className="font-heading text-base font-bold text-white">Integrity-control centre</h3>
        </div>
        <p className="text-sm text-slate-400 mt-1">Operational controls, integrity checks, exceptions and audit evidence across the intelligence lifecycle.</p>
        <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />{data.statement} Evidence cut-off: {data.evidence_cutoff}.</p>
        <p className="text-[11px] font-semibold text-amber-400 mt-1.5 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{data.prototype_notice}</p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`integrity-tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="integrity-tiles">
            {data.overview.map((o) => (
              <button key={o.id} onClick={() => setTab(o.target === "issues" ? "issues" : o.target === "audit" ? "audit" : "overview")}
                className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm text-left hover:border-teal-400 transition-colors" data-testid={`tile-${o.id}`}>
                <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-tight">{o.label}</p>
                <p className={`font-heading text-xl font-bold ${TILE_CLS[o.cls]}`}>{o.count}</p>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">No single "compliance score" is used — each indicator opens its corresponding register. Indicators marked amber/red require action before production use.</p>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="content-type-counters">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Content-type register — AI content counted accurately with its review status</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2">
              {data.content_types.map((c) => (
                <div key={c.label} className="flex items-start gap-2.5">
                  <span className={`font-heading text-lg font-bold w-8 text-right shrink-0 ${TILE_CLS[c.cls]}`}>{c.count}</span>
                  <div><p className="text-[11.5px] font-semibold text-slate-700">{c.label}</p>
                    <p className="text-[10px] text-slate-400 leading-snug">{c.note}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "controls" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={ctlF} onValueChange={setCtlF}>
              <SelectTrigger className="h-8 w-64 text-[11px]" data-testid="control-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All control statuses</SelectItem>
                {data.control_statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[10px] text-slate-400">{controls.length} of {data.controls.length} controls · a written policy is never presented as an automated safeguard</span>
          </div>
          <div className="space-y-2">{controls.map((c) => <ControlRow key={c.id} c={c} />)}</div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="check-register">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Automated-check register — {data.checks.filter((c) => c.mode === "Active").length} active · {data.checks.filter((c) => c.mode === "Simulated").length} simulated (clearly identified; not all checks run in the prototype)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              {data.checks.map((c) => (
                <p key={c.name} className="text-[11px] text-slate-600 flex items-baseline gap-1.5">
                  <span className={`text-[8.5px] font-mono uppercase rounded px-1 shrink-0 ${c.mode === "Active" ? "bg-teal-50 text-teal-700" : "bg-violet-50 text-violet-700"}`}>{c.mode}</span>
                  <span className="font-semibold">{c.name}</span><span className="text-slate-400">— {c.result}</span>
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "citations" && <CitationValidation />}

      {tab === "eligibility" && <EligibilityGates />}

      {tab === "issues" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sevF} onValueChange={setSevF}>
              <SelectTrigger className="h-8 w-40 text-[11px]" data-testid="issue-severity-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all" className="text-xs">All severities</SelectItem>
                {data.severities.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={stF} onValueChange={setStF}>
              <SelectTrigger className="h-8 w-48 text-[11px]" data-testid="issue-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all" className="text-xs">All workflow statuses</SelectItem>
                {data.issue_statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[10px] text-slate-400">{issues.length} of {data.issues.length} — prototype inconsistencies are surfaced here, not concealed behind green labels</span>
          </div>
          <div className="space-y-2">{issues.map((i) => <IssueRow key={i.id} i={i} />)}
            {issues.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No issues match the current filters.</p>}</div>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-3" data-testid="traceability-audit">
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">End-to-end lineage — {data.lineage.object}</p>
            <div className="space-y-1.5 mt-2">
              {data.lineage.chain.map((s, i) => (
                <div key={i} className="flex items-start gap-2.5" data-testid={`lineage-step-${i}`}>
                  <div className="flex flex-col items-center shrink-0 pt-0.5">
                    <span className={`w-2 h-2 rounded-full ${s.valid ? "bg-teal-500" : "bg-red-500"}`} />
                    {i < data.lineage.chain.length - 1 && <span className="w-px h-6 bg-slate-200" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11.5px] text-slate-700"><span className="font-mono text-[9px] uppercase text-slate-400 mr-1.5">{s.step}</span>
                      <span className="font-semibold">{s.id}</span> — {s.label}</p>
                    <p className="text-[10px] text-slate-400">Created by {s.created_by} · {s.date} · Verification: {s.verification} · Review: {s.review} · {s.valid ? "Currently valid" : "Invalidated"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[["Find broken lineage", "0 broken links detected in the current dataset (CTL-01 passing)"],
              ["Find orphan objects", "0 statements without a valid source relationship"],
              ["Find invalidated dependencies", "Downstream flagging active — source exclusion marks linked insights for reassessment (see Source Library review actions)"]].map(([t, r]) => (
              <div key={t} className="bg-white border border-slate-200/80 rounded-xl p-3.5 shadow-sm">
                <p className="text-xs font-bold text-slate-800 flex items-center gap-1">{t}<ArrowRight className="w-3 h-3 text-teal-600" /></p>
                <p className="text-[10.5px] text-slate-500 mt-1">{r}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">Every relationship records its creator, date, verification and review status. Export of a traceability report is available through Reports → Source and Methodology Appendix.</p>
        </div>
      )}

      {tab === "limitations" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="limitations-tab">
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Assessment-level limitations</p>
            <ul className="space-y-1.5">{data.assessment_limitations.map((l, i) => (
              <li key={i} className="text-[11.5px] text-slate-600 flex gap-1.5"><span className="text-amber-500 shrink-0">•</span>{l}</li>))}</ul>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Finding-level limitations — each links to affected findings and confidence assessments</p>
            <div className="space-y-2">
              {data.finding_limitations.map((f, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                  <p className="text-[11.5px] font-semibold text-slate-700">{f.limitation}</p>
                  <p className="text-[10.5px] text-slate-500">Affected: {f.affected.join(", ")}</p>
                  <p className="text-[10.5px] text-amber-700">Operational consequence: {f.consequence}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm lg:col-span-2">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Compliance principles (policy disclosures)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
              {data.principles.map((p, i) => (
                <p key={i} className="text-[11px] text-slate-600 flex gap-1.5"><span className="text-teal-500 shrink-0">•</span><span><span className="font-semibold">{p.principle}:</span> {p.how}</span></p>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Policy statements above are requirements, not evidence of automated enforcement — see the Control Register for operational status.</p>
          </div>
        </div>
      )}
    </div>
  );
}
